/**
 * Canonical life-area registry. Single source of truth for the SIX life
 * areas the backend seeds in `DEFAULT_AREAS` (backend/services/life_allocation.py).
 *
 * Every screen that renders a life area — PetalLifeWheel, MiniLifeWheel,
 * BalanceList, BalanceEditMode, BlockEditModal, ScheduleGoalsScreen,
 * QueuePeek, Reflect, GoalCard, etc. — MUST import from here. Do not
 * hand-roll local AREA_COLORS / LIFE_AREA_META / PETAL_COLOURS.
 *
 * If the backend ever extends the canonical list, update LIFE_AREAS
 * here and the rest of the app picks it up via these exports.
 *
 * Axes on a schedule block (kept separate by design):
 *   life_area        → color (from LIFE_AREA_COLORS)
 *   commitment_level → border treatment (must_do gold / planned none / flexible dashed)
 *   kind             → icon + label only (BLOCK_KIND_LABEL)
 *
 * Blocks with no resolvable life_area (routine / buffer / break / unlinked
 * calendar) render with NEUTRAL_BLOCK_COLOR so the day reads as calm
 * rather than a rainbow.
 */

export const LIFE_AREA_KEYS = [
  "health",
  "career",
  "learning",
  "relationships",
  "finance",
  "personal",
] as const;

export type LifeAreaKey = (typeof LIFE_AREA_KEYS)[number];

export interface LifeAreaMeta {
  key: LifeAreaKey;
  label: string;
  emoji: string;
  /** Hex color used for SVG petals, schedule rails, and timeline tiles. */
  color: string;
  /** Tailwind background class for the colored rail down the side of a chip. */
  rail: string;
  /** Tailwind text color for the chip badge. */
  text: string;
  /** Tailwind tone (border + bg/8) for soft surface panels. */
  tone: string;
  /** Tailwind badge (bg/12 + text) for compact pills. */
  badge: string;
}

/**
 * Order matches the spec (docs/specs/2026-05-02-reflect-balance-redesign.md):
 * Health → Career → Learning → Relationships → Finance → Personal.
 *
 * Keyed by `string` (not the strict LifeAreaKey union) so callers can
 * index with whatever the backend hands them and get `undefined` back
 * for unknown keys. All canonical entries are still typed as LifeAreaMeta.
 */
export const LIFE_AREA_META: Record<string, LifeAreaMeta> = {
  health: {
    key: "health",
    label: "Health",
    emoji: "🏃",
    color: "#B87D7D",
    rail: "bg-rose",
    text: "text-rose",
    tone: "border-rose/25 bg-rose/8",
    badge: "bg-rose/12 text-rose",
  },
  career: {
    key: "career",
    label: "Career",
    emoji: "💼",
    color: "#6366F1",
    rail: "bg-[#6366F1]",
    text: "text-[#6366F1]",
    tone: "border-[#6366F1]/25 bg-[#6366F1]/8",
    badge: "bg-[#6366F1]/12 text-[#6366F1]",
  },
  learning: {
    key: "learning",
    label: "Learning",
    emoji: "📖",
    color: "#5B8BA5",
    rail: "bg-blue",
    text: "text-blue",
    tone: "border-blue/25 bg-blue/8",
    badge: "bg-blue/12 text-blue",
  },
  relationships: {
    key: "relationships",
    label: "Relationships",
    emoji: "🤝",
    color: "#0D9488",
    // UAT 2026-05-25: must use arbitrary-value notation, NOT the
    // `bg-teal` named token. Tailwind JIT only compiles classes it
    // sees as literal strings in source files. Because every consumer
    // pulls these via LIFE_AREA_META[k].rail at runtime, JIT never
    // observes a literal `bg-teal` and silently strips it from the
    // bundle — Relationships then loses its colored dot / pill in
    // BlockEditModal, QueuePeek, GoalCard, etc. The other 5 areas
    // happen to use tokens (`bg-rose`, `bg-amber`, `bg-blue`) that
    // ARE used literally elsewhere, which is why only Relationships
    // broke. Arbitrary-value classes compile per-occurrence and are
    // safe under JIT regardless of indirection.
    rail: "bg-[#0D9488]",
    text: "text-[#0D9488]",
    tone: "border-[#0D9488]/25 bg-[#0D9488]/8",
    badge: "bg-[#0D9488]/12 text-[#0D9488]",
  },
  finance: {
    key: "finance",
    label: "Finance",
    emoji: "💰",
    color: "#C8964C",
    rail: "bg-amber",
    text: "text-amber",
    tone: "border-amber/25 bg-amber/8",
    badge: "bg-amber/12 text-amber",
  },
  personal: {
    key: "personal",
    label: "Personal",
    emoji: "🌱",
    color: "#2A4228",
    rail: "bg-forest",
    text: "text-forest",
    tone: "border-forest/20 bg-sage-pale/40",
    badge: "bg-forest/10 text-forest",
  },
};

/** Hex color map keyed by life-area string. Falls back to NEUTRAL_BLOCK_COLOR. */
export const LIFE_AREA_COLORS: Record<string, string> = Object.fromEntries(
  LIFE_AREA_KEYS.map((k) => [k, LIFE_AREA_META[k].color]),
);

export const NEUTRAL_BLOCK_COLOR = "#8A8078";

/** Bucket key for blocks with no resolvable life_area. */
export const UNLINKED_AREA = "unlinked";
export const UNLINKED_LABEL = "Unlinked";
export const UNLINKED_EMOJI = "📂";

export function lifeAreaColor(area: string | null | undefined): string {
  if (!area) return NEUTRAL_BLOCK_COLOR;
  return LIFE_AREA_COLORS[area] ?? NEUTRAL_BLOCK_COLOR;
}

export function lifeAreaLabel(area: string | null | undefined): string {
  if (!area) return UNLINKED_LABEL;
  const key = area as LifeAreaKey;
  return LIFE_AREA_META[key]?.label ?? area;
}

export function lifeAreaEmoji(area: string | null | undefined): string {
  if (!area) return UNLINKED_EMOJI;
  const key = area as LifeAreaKey;
  return LIFE_AREA_META[key]?.emoji ?? UNLINKED_EMOJI;
}

/**
 * Block-kind labels (NOT life areas — kept here so consumers have one
 * import for everything they need to render a block tile).
 */
export const BLOCK_KIND_LABEL: Record<string, string> = {
  task: "Task",
  custom: "Library",
  buffer: "Routine",
  break: "Recovery",
  calendar: "Fixed",
};

/** Kinds whose visual stays neutral even when a life_area resolves. */
export const NEUTRAL_KINDS: ReadonlySet<string> = new Set(["buffer", "break"]);
